import React, { useState, useEffect, useReducer } from 'react';
import { ThemeProvider } from '@avon/component-library';
import CssBaseline from '@material-ui/core/CssBaseline';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import Helmet from 'react-helmet';

import cookies from 'react-cookies';
import LoginPage from './loginPage';
import HomePage from './home';
import HomeContext from './home/HomeContext';
import mainReducer from './reducers';
import getUrlParams from './utils/getUrlParams';

import gAPi from './utils/api/globalApi';
import {
  getSlProfile,
  getDashboard,
  checkFirstLoginPage,
  getMarkets,
} from './utils/api/endpoints';

const azureKey = window._env_.REACT_APP_AZURE_KEY;
const browserHistory = createBrowserHistory({ basename: '' });
const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
  config: {
    instrumentationKey: azureKey,
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory },
    },
  },
});
if (azureKey) {
  appInsights.loadAppInsights();
}

// handle JT
const domainsToMrkt = {
  'justine.arpqa.avon.com': {
    mrktCd: 'JT', langCd: 'en', langDisplay: 'English', mrktName: 'South Africa - Justine', mrktFlag: 'https://static-qaf.config.avon.com/configApp/flag_ZA.png',
  },
  'justine.arpqaf.avon.com': {
    mrktCd: 'JT', langCd: 'en', langDisplay: 'English', mrktName: 'South Africa - Justine', mrktFlag: 'https://static-qaf.config.avon.com/configApp/flag_ZA.png',
  },
  'justine.arpqap.avon.com': {
    mrktCd: 'JT', langCd: 'en', langDisplay: 'English', mrktName: 'South Africa - Justine', mrktFlag: 'https://static-qaf.config.avon.com/configApp/flag_ZA.png',
  },
  'arp.justine.co.za': {
    mrktCd: 'JT', langCd: 'en', langDisplay: 'English', mrktName: 'South Africa - Justine', mrktFlag: 'https://static-qaf.config.avon.com/configApp/flag_ZA.png',
  },
};


const { domain } = document;
const mrktCfg = domainsToMrkt[domain];

if (mrktCfg) {
  cookies.save('mrktCd', mrktCfg.mrktCd, { path: '/' });
  cookies.save('langCd', mrktCfg.langCd, { path: '/' });
  cookies.save('langDisplay', mrktCfg.langDisplay, { path: '/' });
  cookies.save('mrktName', mrktCfg.mrktName, { path: '/' });
  cookies.save('mrktFlag', mrktCfg.mrktFlag, { path: '/' });
  sessionStorage.setItem('isWebApp', true);
}

const App = () => {
  const initialState = {
    currentPage: 'home',
    profile: sessionStorage.getItem('slProfile'),
    dashboard: sessionStorage.getItem('dashboard'),
    leadsContent: false,
    unreadCount: 0,
    back: 'init', // 'notification', 'viewAllLeads',
  };

  // handle the accout parameters from url
  const {
    mrktCd: qMrktCd, langCd: qLangCd, accessToken: qAccessToken, userId: qUserId,
  } = getUrlParams(window.location.search);

  // const { referrer } = document;
  // const host = `${window.location.protocol}//${window.location.host}`;
  // eslint-disable-next-line max-len
  const isRedirect = qMrktCd && qLangCd && qAccessToken && qUserId;

  const [mrktCd, setMrktCd] = useState(qMrktCd);
  const [langCd, setLangCd] = useState(qLangCd);
  const [accessToken, setAccessToken] = useState(qAccessToken || sessionStorage.getItem('accessToken'));
  const [slProfile, setSlProfile] = useState(sessionStorage.getItem('slProfile'));

  const callApi = gAPi({
    // eslint-disable-next-line no-underscore-dangle
    domain: window._env_.REACT_APP_API_URL,
    market: mrktCd,
    device: 'mobile',
    language: langCd,
    channel: 'mra',
    accessToken,
  });

  const initMrktConfig = async (pathMrktCd, pathLangCd) => {
    cookies.save('mrktCd', pathMrktCd, { path: '/' });
    cookies.save('langCd', pathLangCd, { path: '/' });
    const res = await callApi(getMarkets());
    const markets = res.jsonObject.markets || [];
    const defaultLangDisplay = markets.filter(market => market.mrktCd === pathMrktCd)[0]
      .languages[0].langDsplyNm;
    await cookies.save('langDisplay', defaultLangDisplay, { path: '/' });
    await cookies.save('mrktName', markets.filter(market => market.mrktCd === pathMrktCd)[0].mrktDsplyNm, { path: '/' });
    await cookies.save('mrktFlag', markets.filter(market => market.mrktCd === pathMrktCd)[0].mrktFlgImgUrl, { path: '/' });
  };

  // get the mrktCd & langCd from URL pathname
  const pathParams = window.location.pathname.replace('/index.html', '').split('/');
  if (pathParams.length > 1) {
    const pathMrktCd = pathParams[2];
    const pathLangCd = pathParams[3];
    if (pathMrktCd && pathLangCd) {
      initMrktConfig(pathMrktCd, pathLangCd);
    }
  }

  const initData = async (m, l, u, t) => {
    try {
      await sessionStorage.setItem('accessToken', t);
      await callApi(getDashboard(u, m, l)).then((dashboardData) => {
        sessionStorage.setItem('dashboard', JSON.stringify(dashboardData.jsonObject));
      });

      await callApi(getSlProfile(m, l)).then((resProfile) => {
        sessionStorage.setItem('slProfile', JSON.stringify(resProfile));
        setSlProfile(JSON.stringify(resProfile));
      });

      await callApi(checkFirstLoginPage(u, m)).then((checkFirstLogin) => {
        if (checkFirstLogin.firstLoginPage) {
          sessionStorage.setItem('currentPage', 'landingPage');
        } else {
          sessionStorage.setItem('currentPage', 'home');
        }
      });

      // if (!(qLangCd && qMrktCd && qAccessToken)) {
      // window.location.reload();
      // }
    } catch (err) {
      console.log(err);
    }
  };

  // handle SSO from GI3 by parameters in url
  const curtToken = sessionStorage.getItem('accessToken');
  if (qMrktCd) {
    cookies.save('mrktCd', qMrktCd, { path: '/' });
  }
  if (qLangCd) {
    cookies.save('langCd', qLangCd, { path: '/' });
  }
  if (qUserId) {
    sessionStorage.setItem('userId', qUserId);
  }
  if (qMrktCd && qLangCd && qAccessToken && !curtToken) {
    initData(qMrktCd, qLangCd, qUserId, qAccessToken);
  }

  // handle SSO from GI3
  useEffect(() => {
    window.addEventListener('message', (e) => {
      if (e.data) {
        const {
          mrktCd: gMrktCd, langCd: gLangCd, userId: gUserId, accessToken: gAccessToken,
        } = e.data;
        if (gMrktCd) {
          setMrktCd(gMrktCd);
          cookies.save('mrktCd', gMrktCd, { path: '/' });
        }
        if (gLangCd) {
          setLangCd(gLangCd);
          cookies.save('langCd', gLangCd, { path: '/' });
        }
        if (gUserId) {
          sessionStorage.setItem('userId', gUserId);
        }
        if (gAccessToken) {
          setAccessToken(gAccessToken);
          sessionStorage.setItem('accessToken', gAccessToken);
        }
        if (gMrktCd && gLangCd && gUserId) {
          // fetch the sl profile and set it to session storage
          initData(gMrktCd, gLangCd, gUserId);
        }
      }
    });
  }, []);

  // eslint-disable-next-line
  const PageEle = accessToken && slProfile
    ? (
      <ThemeProvider market={(mrktCfg && mrktCfg.mrktCd) || cookies.load('mrktCd')}>
        <Helmet htmlAttributes={{ lang: 'en' }} />
        <CssBaseline />
        <HomePage />
      </ThemeProvider>
    )
    : (isRedirect ? (
      <ThemeProvider market={(mrktCfg && mrktCfg.mrktCd) || cookies.load('mrktCd')}>
        <Helmet htmlAttributes={{ lang: 'en' }} />
        <CssBaseline />
      </ThemeProvider>
    ) : (
      <ThemeProvider market={(mrktCfg && mrktCfg.mrktCd) || cookies.load('mrktCd')}>
        <Helmet htmlAttributes={{ lang: 'en' }} />
        <CssBaseline />
        <LoginPage mrktCfg={mrktCfg} />
      </ThemeProvider>
    ));

  // console.log(`initialState: ${JSON.stringify(initialState)}`);
  return (
    <HomeContext.Provider value={useReducer(mainReducer, initialState)}>
      {PageEle}
    </HomeContext.Provider>
  );
};

export default App;
